"use strict";
/*
 * Copyright 2026, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.polygonSelectionService = exports.PolygonSelectionService = void 0;
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const turf = __importStar(require("@turf/turf"));
const turf_1 = require("@turf/turf");
const ServiceLocator_1 = __importDefault(require("chaire-lib-common/lib/utils/ServiceLocator"));
const FindOverlappingFeatures_1 = require("chaire-lib-common/lib/services/geodata/FindOverlappingFeatures");
const MapPolygonService_1 = require("chaire-lib-frontend/lib/services/map/MapPolygonService");
const Node_1 = __importDefault(require("transition-common/lib/services/nodes/Node"));
/**
 * Service for managing polygon selection on the map.
 * Handles TerraDraw integration for drawing polygons and selecting nodes within them.
 */
class PolygonSelectionService {
    constructor() {
        this.currentSelectionPolygon = null;
    }
    /**
     * Initialize the TerraDraw polygon drawing service on the map
     * @param map The MapLibre map instance
     */
    initializeDrawService(map) {
        this.draw = (0, MapPolygonService_1.getTerraDraw)(map, (modeChange) => {
            this.handleModeChange(modeChange);
        }, (polygon) => {
            this.handleDrawPolygon(polygon);
        }, (_polygon) => {
            // Delete callback: clear selection when polygon is deleted
            this.clearSelection();
        }, (polygon) => {
            // Update callback: re-select nodes when polygon is modified (e.g., dragged)
            this.handleDrawPolygon(polygon);
        });
    }
    /**
     * Remove the TerraDraw service from the map
     * @param map The MapLibre map instance
     */
    removeDrawService(map) {
        if (this.draw) {
            this.deleteSelectedPolygon();
            (0, MapPolygonService_1.removeTerraDraw)(map, this.draw);
            this.draw = undefined;
        }
    }
    /**
     * Check if the draw service is initialized
     */
    isInitialized() {
        return this.draw !== undefined;
    }
    /**
     * Get the current selection polygon
     */
    getCurrentSelectionPolygon() {
        return this.currentSelectionPolygon;
    }
    /**
     * In the nodes active section, if you click on the map a new node will be created.
     * If the user clicks on the tool for drawing a polygon,
     * selectedNodes will put a value that will prevent a new node from being created.
     * If the user clicks again on the tool for drawing a polygon and selectedNodes doesn't contain nodes (type object),
     * selectedNodes will be cleared so a new node can be created.
     * @param data The next mode, i.e. the mode that Draw is changing to (from terra-draw API)
     */
    handleModeChange(data) {
        if (data.mode && (data.mode === 'draw_polygon' || data.mode === 'polygon' || data.mode === 'select')) {
            // Set the explicitly dedicated flag for drawing mode
            ServiceLocator_1.default.selectedObjectsManager.setSelection('isDrawPolygon', [true]);
        }
        else {
            // When leaving draw mode (simple_select)
            ServiceLocator_1.default.selectedObjectsManager.deselect('isDrawPolygon');
            // Clean up the hack if it was present (legacy cleanup)
            const selectedNodes = ServiceLocator_1.default.selectedObjectsManager.getSingleSelection('nodes');
            if (selectedNodes && typeof selectedNodes !== 'object' && data.mode && data.mode === 'simple_select') {
                ServiceLocator_1.default.selectedObjectsManager.deselect('nodes');
            }
        }
    }
    /**
     * Handle polygon drawing and select nodes within the polygon
     * @param polygonOrCollection The drawn polygon or feature collection
     */
    handleDrawPolygon(polygonOrCollection) {
        var _a;
        this.currentSelectionPolygon = polygonOrCollection;
        // Defensive check: ensure nodes collection exists before accessing
        const nodesCollection = (_a = ServiceLocator_1.default.collectionManager) === null || _a === void 0 ? void 0 : _a.get('nodes');
        if (!nodesCollection) {
            console.error('PolygonSelectionService: nodes collection not available');
            return;
        }
        const allNodes = nodesCollection.getFeatures();
        const overlappingNodesMap = new Map();
        // Normalize input to an array of features
        const features = polygonOrCollection.type === 'FeatureCollection'
            ? polygonOrCollection.features
            : [polygonOrCollection];
        // Process each feature (Polygon or MultiPolygon)
        for (const feature of features) {
            if (feature.geometry.type === 'Polygon') {
                this.findNodesInPolygon(feature, allNodes, overlappingNodesMap);
            }
            else if (feature.geometry.type === 'MultiPolygon') {
                // Explode MultiPolygon into individual Polygons
                // We iterate coordinates manually to avoid potential turf.flatten issues
                // with odd/even polygon counts or winding rules interpreting disjoint polygons as holes.
                for (const coords of feature.geometry.coordinates) {
                    const polygon = turf.polygon(coords);
                    this.findNodesInPolygon(polygon, allNodes, overlappingNodesMap);
                }
            }
        }
        // Filter out frozen nodes (frozen nodes should not be edited, so we remove them from the selection)
        const selectedNodes = Array.from(overlappingNodesMap.values()).filter((node) => {
            return node.get('is_frozen', false) === false && !node.wasFrozen();
        });
        const geojson = selectedNodes.map((node) => (0, cloneDeep_1.default)(node.toGeojson()));
        ServiceLocator_1.default.eventManager.emit('map.updateLayers', {
            transitNodesSelected: (0, turf_1.featureCollection)(geojson)
        });
        ServiceLocator_1.default.selectedObjectsManager.setSelection('nodes', selectedNodes);
        // if at least one selected node was frozen, send info so we can warn the user:
        ServiceLocator_1.default.selectedObjectsManager.setSelection('isContainSelectedFrozenNodes', [
            selectedNodes.length !== overlappingNodesMap.size
        ]);
        ServiceLocator_1.default.selectedObjectsManager.setSelection('isDrawPolygon', [true]);
    }
    /**
     * Find nodes within a polygon and add them to the map (avoiding duplicates)
     */
    findNodesInPolygon(polygon, allNodes, overlappingNodesMap) {
        var _a;
        const nodesInPolygon = (0, FindOverlappingFeatures_1.findOverlappingFeatures)(polygon, allNodes);
        for (const node of nodesInPolygon) {
            const nodeId = (_a = node.properties) === null || _a === void 0 ? void 0 : _a.id;
            if (nodeId && !overlappingNodesMap.has(nodeId)) {
                // Use Node.fromGeojson to properly include geometry in the Node instance
                overlappingNodesMap.set(nodeId, Node_1.default.fromGeojson(node, false, ServiceLocator_1.default.collectionManager));
            }
        }
    }
    /**
     * Clear the polygon selection and deselect all nodes
     */
    clearSelection() {
        this.currentSelectionPolygon = null;
        ServiceLocator_1.default.selectedObjectsManager.deselect('nodes');
        ServiceLocator_1.default.selectedObjectsManager.deselect('isContainSelectedFrozenNodes');
        ServiceLocator_1.default.selectedObjectsManager.deselect('isDrawPolygon');
        ServiceLocator_1.default.eventManager.emit('map.updateLayers', {
            transitNodesSelected: (0, turf_1.featureCollection)([]),
            transitNodes250mRadius: (0, turf_1.featureCollection)([]),
            transitNodes500mRadius: (0, turf_1.featureCollection)([]),
            transitNodes750mRadius: (0, turf_1.featureCollection)([]),
            transitNodes1000mRadius: (0, turf_1.featureCollection)([]),
            transitNodesRoutingRadius: (0, turf_1.featureCollection)([])
        });
    }
    /**
     * Delete the selected polygon from TerraDraw
     */
    deleteSelectedPolygon() {
        if (this.draw) {
            // This will trigger the delete callback, which calls clearSelection
            this.draw.deleteAll().getAll();
            if (this.draw.reset) {
                this.draw.reset();
            }
        }
        else {
            // Only call clearSelection directly when draw is not available
            this.clearSelection();
        }
    }
    /**
     * Called when nodes are updated to re-select nodes within the current polygon
     */
    onNodesUpdated() {
        if (this.currentSelectionPolygon && this.draw) {
            this.handleDrawPolygon(this.currentSelectionPolygon);
        }
    }
    /**
     * Handle draw control based on section change
     * @param map The MapLibre map instance
     * @param section The current active section
     */
    handleSectionChange(map, section) {
        if (section === 'nodes' && !this.draw) {
            this.initializeDrawService(map);
        }
        else if (section !== 'nodes' && this.draw) {
            this.removeDrawService(map);
        }
    }
}
exports.PolygonSelectionService = PolygonSelectionService;
// Export a singleton instance for use across the application
exports.polygonSelectionService = new PolygonSelectionService();
//# sourceMappingURL=PolygonSelectionService.js.map